import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSettings,
  ContrastMode,
} from '@/museum-app/context/settings-context';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group'; // Assuming shadcn/ui toggle-group is available

interface ContrastModeOption {
  value: ContrastMode;
  labelKey: string; // Translation key
  defaultLabel: string; // Default label text
}

const contrastModeOptions: ContrastModeOption[] = [
  {
    value: 'standard', // Assuming 'standard' maps to light mode in CSS
    labelKey: 'settings.contrastMode.standard',
    defaultLabel: 'Standard',
  },
  {
    value: 'high-contrast-bw',
    labelKey: 'settings.contrastMode.highContrastBW',
    defaultLabel: 'High Contrast',
  },
  {
    value: 'dark-mode',
    labelKey: 'settings.contrastMode.darkMode',
    defaultLabel: 'Dark Mode',
  },
  // Note: 'light-mode' class in CSS might be redundant if 'standard' is light
  // If 'standard' is different from 'light-mode', add it here.
  // {
  //   value: 'light-mode',
  //   labelKey: 'settings.contrastMode.lightMode',
  //   defaultLabel: 'Light Mode',
  // },
];

const ContrastModeSelector: React.FC = () => {
  const { contrastMode, setContrastMode } = useSettings();
  const { t } = useTranslation();

  const handleValueChange = (value: string) => {
    if (value) {
      setContrastMode(value as ContrastMode);
    }
  };

  return (
    <ToggleGroup
      type="single"
      value={contrastMode}
      onValueChange={handleValueChange}
      aria-label={t('settings.contrastMode.ariaLabel', 'Select contrast mode')}
      className="flex flex-wrap gap-2"
    >
      {contrastModeOptions.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          aria-label={t(option.labelKey, option.defaultLabel)}
          className="px-4 py-2"
        >
          {t(option.labelKey, option.defaultLabel)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

export default ContrastModeSelector;
